import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Routes, Route, NavLink, useNavigate, Navigate, useLocation, useParams } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, Users, ShieldCheck, Settings, LogOut, Menu, X, Heart, User, Key, Shield, ChevronDown, Palette, ListOrdered, Mail, UserCheck, Archive, FileSpreadsheet, Link2, Database, Server, MessageSquare, Code, Moon, Sun, Activity, ArrowLeft, Newspaper, FolderDown, Cloud, FileBox, BarChart3, ScanLine, ImageIcon, Globe } from 'lucide-react';
import { API } from '../App';
import Dashboard from './Dashboard';
import OffersPage from './OffersPage';
import OfferForm from './OfferForm';
import ProvidersPage from './ProvidersPage';
import ProviderForm from './ProviderForm';
import AdminsPage from './AdminsPage';
import DesignPage, { applyColors, applyFont, FONT_OPTIONS } from './DesignPage';
import DropdownManagementPage from './DropdownManagementPage';
import SmtpPage from './SmtpPage';
import RolesPage from './RolesPage';
import UsersPage from './UsersPage';
import ProfilePage from './ProfilePage';
import ExportPage from './ExportPage';
import ApiKeysPage from './ApiKeysPage';
import IndikationMappingPage from './IndikationMappingPage';
import MariaDbPage from './MariaDbPage';
import VersionPage from './VersionPage';
import TicketsPage from './TicketsPage';
import SecurityPage from './SecurityPage';
import MailTemplatePage from './MailTemplatePage';
import MailSignaturePage from './MailSignaturePage';
import MailComposerPage from './MailComposerPage';
import MailLogPage from './MailLogPage';
import AuditLogPage from './AuditLogPage';
import ProviderHomePage from './ProviderHomePage';
import NewsPage from './NewsPage';
import DownloadsPage from './DownloadsPage';
import CloudSettingsPage from './CloudSettingsPage';
import DownloadSettingsPage from './DownloadSettingsPage';
import GbmManagementPage from './GbmManagementPage';
import ProviderDocumentsPage from './ProviderDocumentsPage';
import ManagerHomePage from './ManagerHomePage';
import ManagersPage from './ManagersPage';
import ManagerDocumentsPage from './ManagerDocumentsPage';
import SurveysPage from './SurveysPage';
import SurveyFormPage from './SurveyFormPage';
import VerificationLogPage from './VerificationLogPage';

import ProviderFoldersOverview from './ProviderFoldersOverview';
import GeneralSettingsPage from './GeneralSettingsPage';
import LoginPageSettingsPage from './LoginPageSettingsPage';
import HerzgruppensucheSettingsPage from './HerzgruppensucheSettingsPage';
const UserContext = createContext(null);
export function useUserContext() { return useContext(UserContext); }

const DesignContext = createContext({ colors: [], fonts: [] });
export function useDesignColors() { return useContext(DesignContext).colors; }
export function useDesignFonts() { return useContext(DesignContext).fonts; }

/** Clear session data from localStorage while preserving persistent items like trusted device tokens. */
function clearSession() {
  const trustedDevice = localStorage.getItem('bgpr_trusted_device');
  localStorage.clear();
  if (trustedDevice) {
    localStorage.setItem('bgpr_trusted_device', trustedDevice);
  }
}

function ProviderDocumentsPageWrapper() {
  const { pid } = useParams();
  return <ProviderDocumentsPage providerId={parseInt(pid)} />;
}

export function useApi() {
  const token = localStorage.getItem('bgpr_token');

  const apiFetch = useCallback(async (path, options = {}) => {
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers };
    if (options.body instanceof FormData) delete headers['Content-Type'];
    const res = await fetch(`${API}${path}`, { ...options, headers });
    if (res.status === 401) { clearSession(); window.location.href = '/login'; return; }
    return res;
  }, [token]);

  return { apiFetch, token };
}

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [offersOpen, setOffersOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [siteTitle, setSiteTitle] = useState('BGPR Verwaltung');
  const [appVersion, setAppVersion] = useState('');
  const [perms, setPerms] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('bgpr_dark_mode') === '1');
  const [designColors, setDesignColors] = useState([]);
  const [designFonts, setDesignFonts] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { apiFetch } = useApi();
  const userType = parseInt(localStorage.getItem('bgpr_user_type') || '0');
  const isImpersonating = !!localStorage.getItem('bgpr_impersonating');
  const impersonatingFrom = localStorage.getItem('bgpr_impersonating') || '';

  // Dark mode effect
  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
    localStorage.setItem('bgpr_dark_mode', darkMode ? '1' : '0');
  }, [darkMode]);

  const endImpersonation = () => {
    const origToken = localStorage.getItem('bgpr_original_token');
    const origType = localStorage.getItem('bgpr_original_user_type');
    const origEmail = localStorage.getItem('bgpr_original_email');
    if (origToken) {
      localStorage.setItem('bgpr_token', origToken);
      localStorage.setItem('bgpr_user_type', origType);
      localStorage.setItem('bgpr_email', origEmail);
    }
    localStorage.removeItem('bgpr_original_token');
    localStorage.removeItem('bgpr_original_user_type');
    localStorage.removeItem('bgpr_original_email');
    localStorage.removeItem('bgpr_impersonating');
    // Return to the appropriate list based on the impersonated user type
    const currentType = parseInt(localStorage.getItem('bgpr_user_type'));
    window.location.href = '/app';
  };

  // Permission helper
  const can = (area, level = 'ansicht') => {
    if (userType === 1) return true; // Super-Admin always
    if (!perms) return false;
    const areaLevel = perms[area];
    if (!areaLevel || areaLevel === 'keine') return false;
    if (level === 'ansicht') return areaLevel === 'ansicht' || areaLevel === 'bearbeiten';
    return areaLevel === 'bearbeiten';
  };

  // Auto-expand settings submenu when on a settings page
  useEffect(() => {
    if (location.pathname.startsWith('/app/settings')) setSettingsOpen(true);
    if (location.pathname.startsWith('/app/offers')) setOffersOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    if (!localStorage.getItem('bgpr_token')) { navigate('/login'); return; }
    apiFetch('/api/auth/me').then(r => r?.json()).then(d => d && setUser(d));
    // Load and apply design config
    apiFetch('/api/settings/design-config').then(r => r?.json()).then(d => {
      if (d?.colors) {
        applyColors(d.colors);
        const uniqueColors = [...new Set(Object.values(d.colors).filter(c => c && typeof c === 'string'))];
        setDesignColors(uniqueColors);
      }
      if (d?.font) applyFont(d.font);
      if (d?.seo?.title) { setSiteTitle(d.seo.title); document.title = d.seo.title; }
    });
    // Load user permissions
    apiFetch('/api/settings/roles/permissions-for-user').then(r => r?.json()).then(d => {
      if (d?.permissions) setPerms(d.permissions);
    });
    // Load version for footer display
    fetch(`${API}/api/settings/version/public`).then(r => r.json()).then(d => {
      if (d?.version) setAppVersion(d.version);
    }).catch(() => {});
  }, []);

  // Session timeout - auto logout after inactivity
  useEffect(() => {
    let timeoutId;
    let timeoutMs = 480 * 60 * 1000; // default 8h for admins

    const loadTimeout = async () => {
      // Type 3 (providers): fixed 60 min inactivity timeout
      if (userType === 3) {
        timeoutMs = 60 * 60 * 1000;
        resetTimer();
        return;
      }
      try {
        const r = await fetch(`${API}/api/settings/security/public`);
        const d = await r.json();
        if (d?.session_timeout_minutes) timeoutMs = d.session_timeout_minutes * 60 * 1000;
      } catch {}
      resetTimer();
    };

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        clearSession();
        window.location.href = '/login?timeout=1';
      }, timeoutMs);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, resetTimer));
    loadTimeout();

    return () => {
      clearTimeout(timeoutId);
      events.forEach(e => document.removeEventListener(e, resetTimer));
    };
  }, []);

  const logout = () => { clearSession(); navigate('/login'); };
  const roleLabel = userType === 1 ? 'Super Admin' : userType === 2 ? 'Admin' : userType === 4 ? 'Verwalter' : 'Anbieter';
  const today = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  if (!localStorage.getItem('bgpr_token')) return <Navigate to="/login" />;

  return (
    <UserContext.Provider value={{ user, userType }}>
    <DesignContext.Provider value={{ colors: designColors, fonts: FONT_OPTIONS }}>
    <div className="dashboard" data-testid="dashboard">
      {/* Sidebar */}
      <nav className={`sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-icon"><Heart size={18} color="#fff" fill="#fff" /></div>
          <div>
            <h4>{siteTitle.split(' ')[0] || 'BGPR'}</h4>
            <span>{siteTitle.split(' ').slice(1).join(' ') || 'Verwaltung'}</span>
          </div>
        </div>

        <ul className="sidebar-nav">
          {can('dashboard') && userType !== 3 && userType !== 4 && <li><NavLink to="/app" end onClick={() => setSidebarOpen(false)} data-testid="nav-dashboard"><LayoutDashboard size={20} /> Dashboard</NavLink></li>}
          {(userType === 3 || userType === 4) && <li><NavLink to="/app" end onClick={() => setSidebarOpen(false)} data-testid="nav-my-area"><User size={20} /> Mein Bereich</NavLink></li>}
          {can('angebote') && userType !== 3 && userType !== 4 && (
            <li>
              <button
                className={`sidebar-submenu-toggle ${offersOpen ? 'open' : ''}`}
                onClick={() => setOffersOpen(!offersOpen)}
                data-testid="nav-offers-toggle"
              >
                <ClipboardList size={20} /> Angebote <ChevronDown size={16} className={`submenu-chevron ${offersOpen ? 'rotated' : ''}`} />
              </button>
              {offersOpen && (
                <ul className="sidebar-submenu" data-testid="offers-submenu">
                  <li><NavLink to="/app/offers" end onClick={() => setSidebarOpen(false)} data-testid="nav-offers-all"><ClipboardList size={18} /> Alle Angebote</NavLink></li>
                  <li><NavLink to="/app/offers/certified" onClick={() => setSidebarOpen(false)} data-testid="nav-offers-certified"><ShieldCheck size={18} /> Zertifizierte</NavLink></li>
                  <li><NavLink to="/app/offers/uncertified" onClick={() => setSidebarOpen(false)} data-testid="nav-offers-uncertified"><ShieldCheck size={18} /> Nicht zertifizierte</NavLink></li>
                  {can('admin_verwaltung') && <li><NavLink to="/app/offers/archived" onClick={() => setSidebarOpen(false)} data-testid="nav-offers-archived"><Archive size={18} /> Archivierte</NavLink></li>}
                </ul>
              )}
            </li>
          )}
          {can('anbieter') && <li><NavLink to="/app/providers" onClick={() => setSidebarOpen(false)} data-testid="nav-providers"><Users size={20} /> Anbieter</NavLink></li>}
          <li><NavLink to="/app/news" onClick={() => setSidebarOpen(false)} data-testid="nav-news"><Newspaper size={20} /> News</NavLink></li>
          <li><NavLink to="/app/downloads" onClick={() => setSidebarOpen(false)} data-testid="nav-downloads"><FolderDown size={20} /> Downloads</NavLink></li>
          {(userType <= 2 || userType === 4) && <li><NavLink to="/app/gbm" onClick={() => setSidebarOpen(false)} data-testid="nav-gbm"><Shield size={20} /> GBM-Verwaltung</NavLink></li>}
          {(userType === 3 || userType === 4) && <li><NavLink to="/app/my-documents" onClick={() => setSidebarOpen(false)} data-testid="nav-my-documents"><FileBox size={20} /> Unsere Dokumente</NavLink></li>}
          {(userType === 3 || userType === 4) && <li><NavLink to="/app/survey" onClick={() => setSidebarOpen(false)} data-testid="nav-survey-form"><BarChart3 size={20} /> Bestandserhebung</NavLink></li>}
          {userType <= 2 && <li><NavLink to="/app/provider-documents-overview" onClick={() => setSidebarOpen(false)} data-testid="nav-provider-docs-overview"><FileBox size={20} /> Anbieter-Dokumente</NavLink></li>}
          {can('admin_verwaltung') && (
            <li>
              <button
                className={`sidebar-submenu-toggle ${adminOpen ? 'open' : ''}`}
                onClick={() => setAdminOpen(!adminOpen)}
                data-testid="nav-admin-toggle"
              >
                <ShieldCheck size={20} /> Administration <ChevronDown size={16} className={`submenu-chevron ${adminOpen ? 'rotated' : ''}`} />
              </button>
              {adminOpen && (
                <ul className="sidebar-submenu" data-testid="admin-submenu">
                  <li><NavLink to="/app/surveys" onClick={() => setSidebarOpen(false)} data-testid="nav-surveys"><BarChart3 size={18} /> Bestandserhebung</NavLink></li>
                  <li><NavLink to="/app/verification-log" onClick={() => setSidebarOpen(false)} data-testid="nav-verification-log"><ScanLine size={18} /> QR-Protokoll</NavLink></li>
                  <li><NavLink to="/app/export/vdek" onClick={() => setSidebarOpen(false)} data-testid="nav-export-vdek"><FileSpreadsheet size={18} /> vdek-Export</NavLink></li>
                  <li><NavLink to="/app/mail/compose" onClick={() => setSidebarOpen(false)} data-testid="nav-mail-compose"><Mail size={18} /> E-Mail verfassen</NavLink></li>
                  <li><NavLink to="/app/mail/log" onClick={() => setSidebarOpen(false)} data-testid="nav-mail-log"><Mail size={18} /> E-Mail-Protokoll</NavLink></li>
                  <li><NavLink to="/app/users" onClick={() => setSidebarOpen(false)} data-testid="nav-users"><UserCheck size={18} /> Benutzer</NavLink></li>
                  <li><NavLink to="/app/managers" onClick={() => setSidebarOpen(false)} data-testid="nav-managers"><Users size={18} /> Verwalter</NavLink></li>
                  <li><NavLink to="/app/admins" onClick={() => setSidebarOpen(false)} data-testid="nav-admins"><ShieldCheck size={18} /> Admin verwalten</NavLink></li>
                  <li><NavLink to="/app/audit-log" onClick={() => setSidebarOpen(false)} data-testid="nav-audit-log"><Activity size={18} /> Aktivitätslog</NavLink></li>
                </ul>
              )}
            </li>
          )}
          {can('einstellungen') && (
            <li>
              <button
                className={`sidebar-submenu-toggle ${settingsOpen ? 'open' : ''}`}
                onClick={() => setSettingsOpen(!settingsOpen)}
                data-testid="nav-settings-toggle"
              >
                <Settings size={20} /> Einstellungen <ChevronDown size={16} className={`submenu-chevron ${settingsOpen ? 'rotated' : ''}`} />
              </button>
              {settingsOpen && (
                <ul className="sidebar-submenu" data-testid="settings-submenu">
                  <li><NavLink to="/app/settings/general" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-general"><Settings size={18} /> Allgemein</NavLink></li>
                  <li><NavLink to="/app/settings/design" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-design"><Palette size={18} /> Design</NavLink></li>
                  <li><NavLink to="/app/settings/login-page" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-login-page"><ImageIcon size={18} /> Login-Seite</NavLink></li>
                  <li><NavLink to="/app/settings/herzgruppensuche" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-herzgruppensuche"><Globe size={18} /> Herzgruppensuche</NavLink></li>
                  <li><NavLink to="/app/settings/dropdowns" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-dropdowns"><ListOrdered size={18} /> Dropdown-Verwaltung</NavLink></li>
                  <li><NavLink to="/app/settings/smtp" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-smtp"><Mail size={18} /> E-Mail (SMTP)</NavLink></li>
                  <li><NavLink to="/app/settings/mail-templates" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-mail-templates"><Mail size={18} /> E-Mail-Vorlagen</NavLink></li>
                  <li><NavLink to="/app/settings/mail-signature" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-mail-signature"><Code size={18} /> E-Mail-Signatur</NavLink></li>
                  <li><NavLink to="/app/settings/roles" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-roles"><Shield size={18} /> Rollen & Berechtigungen</NavLink></li>
                  <li><NavLink to="/app/settings/indikation" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-indikation"><ListOrdered size={18} /> Indikation & Positionsnr.</NavLink></li>
                  <li><NavLink to="/app/settings/api-keys" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-api-keys"><Link2 size={18} /> API-Schlüssel</NavLink></li>
                  <li><NavLink to="/app/settings/mariadb" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-mariadb"><Database size={18} /> MariaDB</NavLink></li>
                  <li><NavLink to="/app/settings/version" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-version"><Server size={18} /> System-Info</NavLink></li>
                  <li><NavLink to="/app/settings/security" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-security"><Shield size={18} /> Sicherheit</NavLink></li>
                  <li><NavLink to="/app/settings/cloud" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-cloud"><Cloud size={18} /> Cloud-Speicher</NavLink></li>
                  <li><NavLink to="/app/settings/downloads" onClick={() => setSidebarOpen(false)} data-testid="nav-settings-downloads"><FolderDown size={18} /> Download-Bereich</NavLink></li>
                </ul>
              )}
            </li>
          )}
        </ul>

        {/* User profile at bottom */}
        <div className="sidebar-user">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="sidebar-user-avatar"><User size={18} /></div>
              <div className="sidebar-user-info">
                <h5>{user?.email || user?.verein || user?.club_id || 'Benutzer'}</h5>
                <p>{roleLabel}</p>
              </div>
            </div>
            <div className="sidebar-user-actions">
              <button onClick={() => navigate('/app/profile')} title="Profil"><Key size={16} /></button>
              <button onClick={() => navigate('/app/profile#2fa')} title="Sicherheit / 2FA" data-testid="nav-2fa"><Shield size={16} /></button>
              <button onClick={logout} title="Abmelden" data-testid="nav-logout"><LogOut size={16} /></button>
            </div>
          </div>
        </div>
      </nav>

      {sidebarOpen && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 99 }} onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="main-area">
        {isImpersonating && (
          <div className="impersonation-banner" data-testid="impersonation-banner">
            Sie sind als anderer Benutzer angemeldet (von {impersonatingFrom})
            <button onClick={endImpersonation} data-testid="end-impersonation-btn">Zurück zum Admin</button>
          </div>
        )}
        <div className="topbar">
          <div className="topbar-left">
            <button className="mobile-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>{sidebarOpen ? <X size={22} /> : <Menu size={22} />}</button>
          </div>
          <div className="topbar-right">
            <button className="theme-toggle" onClick={() => setDarkMode(!darkMode)} data-testid="theme-toggle" title={darkMode ? 'Hellmodus' : 'Dunkelmodus'}>
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={() => navigate('/app/tickets')} data-testid="nav-tickets"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: 'none', background: 'var(--primary)', fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--primary)'; }}>
              <MessageSquare size={15} /> Tickets
            </button>
            <span className="topbar-date">{today}</span>
          </div>
        </div>

        <div className="content">
          <Routes>
            <Route index element={userType === 4 ? <ManagerHomePage user={user} /> : (userType === 3 ? <ProviderHomePage user={user} setUser={setUser} /> : <Dashboard userType={userType} />)} />
            <Route path="offers" element={<OffersPage userType={userType} certFilter="all" />} />
            <Route path="offers/certified" element={<OffersPage userType={userType} certFilter="1" />} />
            <Route path="offers/uncertified" element={<OffersPage userType={userType} certFilter="0" />} />
            <Route path="offers/archived" element={<OffersPage userType={userType} certFilter="archived" />} />
            <Route path="offers/new" element={<OfferForm userType={userType} />} />
            <Route path="offers/:id/edit" element={<OfferForm userType={userType} />} />
            <Route path="export/vdek" element={<ExportPage />} />
            <Route path="mail/compose" element={<MailComposerPage />} />
            <Route path="mail/log" element={<MailLogPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
            <Route path="providers" element={<ProvidersPage />} />
            <Route path="providers/new" element={<ProviderForm />} />
            <Route path="providers/:id/edit" element={<ProviderForm />} />
            <Route path="admins" element={<AdminsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="managers" element={<ManagersPage />} />
            <Route path="settings" element={<Navigate to="/app/settings/general" replace />} />
            <Route path="settings/general" element={<GeneralSettingsPage />} />
            <Route path="settings/design" element={<DesignPage onTitleChange={(t) => { setSiteTitle(t); document.title = t; }} />} />
            <Route path="settings/login-page" element={<LoginPageSettingsPage />} />
            <Route path="settings/herzgruppensuche" element={<HerzgruppensucheSettingsPage />} />
            <Route path="settings/backup" element={<Navigate to="/app/settings/mariadb" replace />} />
            <Route path="settings/dropdowns" element={<DropdownManagementPage />} />
            <Route path="settings/smtp" element={<SmtpPage />} />
            <Route path="settings/roles" element={<RolesPage />} />
            <Route path="settings/indikation" element={<IndikationMappingPage />} />
            <Route path="settings/api-keys" element={<ApiKeysPage />} />
            <Route path="settings/mariadb" element={<MariaDbPage />} />
            <Route path="settings/version" element={<VersionPage />} />
            <Route path="settings/security" element={<SecurityPage />} />
            <Route path="settings/mail-templates" element={<MailTemplatePage />} />
            <Route path="settings/mail-signature" element={<MailSignaturePage />} />
            <Route path="settings/cloud" element={<CloudSettingsPage />} />
            <Route path="settings/downloads" element={<DownloadSettingsPage />} />
            <Route path="gbm" element={<GbmManagementPage />} />
            <Route path="profile" element={<ProfilePage user={user} setUser={setUser} />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="news" element={<NewsPage userType={userType} />} />
            <Route path="downloads" element={<DownloadsPage userType={userType} />} />
            <Route path="my-documents" element={userType === 4 ? <ManagerDocumentsPage /> : <ProviderDocumentsPage />} />
            <Route path="provider-documents/:pid" element={<ProviderDocumentsPageWrapper />} />
            <Route path="provider-documents-overview" element={<ProviderFoldersOverview />} />
            <Route path="surveys" element={<SurveysPage />} />
            <Route path="verification-log" element={<VerificationLogPage />} />
            <Route path="survey" element={<SurveyFormPage user={user} />} />
          </Routes>
        </div>

        <div className="app-footer">&copy; 2026 BGPR e.V. - {siteTitle}{appVersion && <span style={{ marginLeft: 8, opacity: 0.6 }}>v{appVersion}</span>}</div>
      </div>
    </div>
    </DesignContext.Provider>
    </UserContext.Provider>
  );
}
